/**
 * Silent / Vibrate / Do-Not-Disturb policy for the app's OWN in-app alerts.
 *
 * The in-app message chime and foreground call ringtone play through the
 * WebView's media element (the MEDIA stream on Android), which the phone's
 * Silent mode does not mute — so the app has to decide for itself. The native
 * `RingerMode` plugin reports the ringer state; these pure functions turn it
 * into play / vibrate decisions.
 *
 * Owner decision (build 128/90):
 *  - message chime ........ only when mode = normal and DND is off (ANY DND mode silences)
 *  - message vibration .... mode normal or vibrate, and DND is off (ANY DND mode silences)
 *  - in-app call ringtone . only when mode = normal and DND is off OR Priority-only
 *  - in-app call vibration  mode normal or vibrate, and DND is off OR Priority-only
 *    (Priority-only DND still lets Ascend calls ring in-app; only Total silence and
 *    Alarms-only silence the in-app ring / call vibration.)
 * Native Telecom / CallKit ringing is governed by the OS and is NOT touched here.
 * Urgent / on-call rings never go through these functions — they keep breaking
 * through Silent / DND (pinned by use-voice-call-ringtone.urgent.test.tsx).
 *
 * `unknown` (web, iOS — there is no public mute-switch API — or a plugin
 * failure) means "cannot tell": treated as normal so we never swallow an alert
 * we were not told to. iOS chimes are silenced by the mute switch through an
 * `.ambient` AVAudioSession instead (see RingerModePlugin.swift).
 */

export type RingerMode = 'normal' | 'vibrate' | 'silent' | 'unknown'

/** Android interruption filter: off = ALL, priority = PRIORITY, total = NONE, alarms = ALARMS. */
export type DndMode = 'off' | 'priority' | 'total' | 'alarms'

export type RingerState = {
  mode: RingerMode
  /** Do Not Disturb (any interruption filter other than "allow all"). */
  dnd: boolean
  /**
   * Which DND flavour. Optional for older payloads: `dnd: true` without it is
   * treated as `total` (the most silencing reading).
   */
  dndMode?: DndMode
}

export const UNKNOWN_RINGER_STATE: RingerState = { mode: 'unknown', dnd: false }

function isDndMode(value: unknown): value is DndMode {
  return value === 'off' || value === 'priority' || value === 'total' || value === 'alarms'
}

/** Effective DND flavour of a state (derives it from the boolean when absent). */
export function effectiveDndMode(state: RingerState): DndMode {
  if (!state.dnd) return 'off'
  // dnd=true but no (or contradictory 'off') flavour → most silencing reading.
  return state.dndMode && state.dndMode !== 'off' ? state.dndMode : 'total'
}

/** Coerce an untrusted native payload into a RingerState (fail open to unknown). */
export function normalizeRingerState(raw: unknown): RingerState {
  if (!raw || typeof raw !== 'object') return UNKNOWN_RINGER_STATE
  const { mode, dnd, dndMode } = raw as { mode?: unknown; dnd?: unknown; dndMode?: unknown }
  const normalizedMode: RingerMode =
    mode === 'normal' || mode === 'vibrate' || mode === 'silent' ? mode : 'unknown'
  const isDnd = dnd === true
  const normalizedDnd: DndMode = !isDnd
    ? 'off'
    : isDndMode(dndMode) && dndMode !== 'off'
      ? dndMode
      : 'total'
  return { mode: normalizedMode, dnd: isDnd, dndMode: normalizedDnd }
}

function isLoudMode(state: RingerState): boolean {
  return state.mode === 'normal' || state.mode === 'unknown'
}

function allowsVibrationMode(state: RingerState): boolean {
  return state.mode === 'normal' || state.mode === 'vibrate' || state.mode === 'unknown'
}

/** Messages: ANY DND silences. */
function noDnd(state: RingerState): boolean {
  return effectiveDndMode(state) === 'off'
}

/** Calls: Priority-only DND still lets Ascend calls ring in-app. */
function callsAllowedByDnd(state: RingerState): boolean {
  const dnd = effectiveDndMode(state)
  return dnd === 'off' || dnd === 'priority'
}

/** In-app new-message chime. */
export function shouldPlayMessageChime(state: RingerState): boolean {
  return isLoudMode(state) && noDnd(state)
}

/** Haptics for an incoming message. */
export function shouldVibrateForMessage(state: RingerState): boolean {
  return allowsVibrationMode(state) && noDnd(state)
}

/** In-app (WebView-played) ringtone for an incoming call. */
export function shouldPlayInAppRingtone(state: RingerState): boolean {
  return isLoudMode(state) && callsAllowedByDnd(state)
}

/** In-app vibration for an incoming call. */
export function shouldVibrateForCall(state: RingerState): boolean {
  return allowsVibrationMode(state) && callsAllowedByDnd(state)
}
